/**
 * Determines whether the investor APIs are configured and reachable enough to enable investor flows.
 */
import type { ResolvedRestApiRuntime } from "./investor-api-config";
import {
  getIdentityApiRuntime,
  getPlaidApiRuntime,
} from "./investor-api-config";

const API_ENDPOINT_REACHABILITY_TIMEOUT_MS = 2500;
const API_HEALTHCHECK_PATH = "checkhealth";

export type InvestorApiReadiness = {
  ready: boolean;
  plaidReady: boolean;
  identityReady: boolean;
};

async function isEndpointReachable(endpoint: string): Promise<boolean> {
  if (process.env.NODE_ENV === "test") {
    return true;
  }

  if (typeof fetch !== "function") {
    return true;
  }

  const endpointUrl = endpoint.trim();
  if (!endpointUrl) {
    return false;
  }
  const healthCheckUrl = getHealthCheckUrl(endpointUrl);

  const abortController = new AbortController();
  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, API_ENDPOINT_REACHABILITY_TIMEOUT_MS);

  try {
    await fetch(healthCheckUrl, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      signal: abortController.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

function getHealthCheckUrl(endpoint: string): string {
  try {
    const baseUrl = endpoint.endsWith("/") ? endpoint : `${endpoint}/`;
    return new URL(API_HEALTHCHECK_PATH, baseUrl).toString();
  } catch {
    return endpoint;
  }
}

async function isApiRuntimeReachable(
  apiRuntime: ResolvedRestApiRuntime,
  apiLabel: string,
): Promise<boolean> {
  const reachable = await isEndpointReachable(apiRuntime.endpoint);
  if (!reachable) {
    console.warn(
      `${apiLabel} API not ready for investor flows: endpoint unreachable for alias "${apiRuntime.alias}" (${apiRuntime.endpoint})`,
    );
  }
  return reachable;
}

export async function getInvestorApiReadiness(): Promise<InvestorApiReadiness> {
  const [plaidResult, identityResult] = await Promise.allSettled([
    getPlaidApiRuntime(),
    getIdentityApiRuntime(),
  ]);

  let plaidReady = plaidResult.status === "fulfilled";
  let identityReady = identityResult.status === "fulfilled";

  if (plaidResult.status === "rejected") {
    console.warn("Plaid API not ready for investor flows", plaidResult.reason);
  }

  if (identityResult.status === "rejected") {
    console.warn(
      "Identity API not ready for investor flows",
      identityResult.reason,
    );
  }

  const [plaidReachable, identityReachable] = await Promise.all([
    plaidResult.status === "fulfilled"
      ? isApiRuntimeReachable(plaidResult.value, "Plaid")
      : Promise.resolve(false),
    identityResult.status === "fulfilled"
      ? isApiRuntimeReachable(identityResult.value, "Identity")
      : Promise.resolve(false),
  ]);

  plaidReady = plaidReady && plaidReachable;
  identityReady = identityReady && identityReachable;

  return {
    ready: plaidReady && identityReady,
    plaidReady,
    identityReady,
  };
}
